import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { UserModeService } from 'src/app/services/customs/user-mode/user-mode.service';
import { DashboardService } from 'src/app/services/modules/dashboards/dashboard.service';
import { FinancialsService } from 'src/app/services/modules/financials/financials.service';
import { FinancialComponent } from './components/financial/financial.component';
import { formatMoney } from './components/financial/financial-grid';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-financials',
  templateUrl: './financials.component.html',
  styleUrl: './financials.component.scss',
})
export class FinancialsComponent implements OnInit, OnDestroy {
  @ViewChild(FinancialComponent) private financial?: FinancialComponent;

  currentUserMode: any = 'customer';
  pendingCancellations = 0;
  /** Decimal strings from the summary endpoint, kept as sent and only formatted for display. */
  netEarnings: string | null = null;
  pendingPayout: string | null = null;
  summaryLoading = true;
  private summaryRequested = false;
  private overviewRequested = false;
  private readonly destroy$ = new Subject<void>();
  readonly cancellationUrl = `${environment.appUrl}/provider/jobs?pagetype=cancellation`;
  readonly helpUrl = 'https://help.truckit.net/support/solutions/articles/35000175754-what-is-the-truckit-reference-number-on-my-bank-statement-';

  constructor(
    private userModeService: UserModeService,
    private dashboardService: DashboardService,
    private financialsService: FinancialsService,
  ) {}

  /** Mirrors the Sales toolbar export so the mobile header button can drive it. */
  get exporting(): boolean {
    return !!this.financial?.exporting;
  }

  exportCsv(): void {
    this.financial?.exportCsv();
  }

  ngOnInit(): void {
    // mode$ emits again whenever the user object refreshes; only a real change of mode matters here.
    this.userModeService.mode$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((mode) => {
      if (!mode) return;

      this.currentUserMode = mode;
      if (mode === 'provider') {
        this.loadOverview();
        this.loadSummary();
      } else {
        // Switching back to provider later must show fresh figures, not the ones from before the switch.
        this.overviewRequested = false;
        this.summaryRequested = false;
        this.summaryLoading = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOverview(): void {
    if (this.overviewRequested) {
      return;
    }
    this.overviewRequested = true;
    this.dashboardService.getDashboardOverview().subscribe({
      next: (response) => {
        const overview = response?.data ?? {};
        this.pendingCancellations = Number(overview.pending_cancelation ?? 0);
      },
    });
  }

  /** Page-level figures: fetched once, apart from the grid, so filters, sorting and paging never touch them. */
  private loadSummary(): void {
    if (this.summaryRequested) {
      return;
    }
    this.summaryRequested = true;
    this.financialsService.getProviderFinancialSummary().subscribe({
      next: (response) => {
        const ok = response?.status !== false;
        this.netEarnings = ok ? toDecimalString(response?.net_earnings) : null;
        this.pendingPayout = ok ? toDecimalString(response?.pending_payout) : null;
        this.summaryLoading = false;
      },
      // The cards show "—"; the grid is a separate request and carries on.
      error: () => {
        this.summaryLoading = false;
      },
    });
  }

  /** "$113,349.01", or "—" when there is no value. */
  money(value: string | null): string {
    return value === null ? '—' : formatMoney(value) || '—';
  }
}

function toDecimalString(value: unknown): string | null {
  return value === null || value === undefined || value === '' ? null : String(value);
}
